
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDistanceToNow } from 'date-fns';
import type { SecurityEvent } from '@/hooks/useSecurityCompliance';
import type { SecurityFilters } from '@/hooks/useSecurityFilters';

interface SecurityEventsTabProps {
  events: SecurityEvent[];
  filters: SecurityFilters;
  onUpdateFilter: (key: keyof SecurityFilters, value: string) => void;
  onResolveEvent: (eventId: string) => void;
  loading: boolean;
}

const SecurityEventsTab = ({ 
  events, 
  filters, 
  onUpdateFilter, 
  onResolveEvent, 
  loading 
}: SecurityEventsTabProps) => {
  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, any> = {
      low: 'outline',
      medium: 'secondary',
      high: 'default',
      critical: 'destructive'
    };

    const colors: Record<string, string> = {
      low: 'text-green-600',
      medium: 'text-black',
      high: 'text-black',
      critical: 'text-red-600'
    };

    return (
      <Badge variant={variants[severity]} className={colors[severity]}>
        {severity.toUpperCase()}
      </Badge>
    );
  };

  const filteredEvents = events.filter(event => {
    if (filters.severity !== 'all' && event.severity !== filters.severity) return false;
    if (filters.eventType !== 'all' && event.event_type !== filters.eventType) return false;
    if (filters.searchTerm && !event.event_type.toLowerCase().includes(filters.searchTerm.toLowerCase())) return false;
    return true;
  });

  const eventTypeCounts = events.reduce((acc, event) => {
    acc[event.event_type] = (acc[event.event_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Select
          value={filters.severity}
          onValueChange={(value) => onUpdateFilter('severity', value)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.eventType}
          onValueChange={(value) => onUpdateFilter('eventType', value)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by event type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.keys(eventTypeCounts).map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event Type</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>Risk Score</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEvents.map((event) => (
              <TableRow key={event.id}>
                <TableCell className="font-medium">{event.event_type}</TableCell>
                <TableCell>{getSeverityBadge(event.severity)}</TableCell>
                <TableCell>
                  <Badge variant={event.risk_score > 70 ? 'destructive' : event.risk_score > 40 ? 'default' : 'outline'}>
                    {event.risk_score}
                  </Badge>
                </TableCell>
                <TableCell>{event.user_id ? event.user_id.substring(0, 8) + '...' : 'System'}</TableCell>
                <TableCell>{formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}</TableCell>
                <TableCell>
                  <Badge variant={event.resolved ? 'outline' : 'destructive'}>
                    {event.resolved ? 'Resolved' : 'Open'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {!event.resolved && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onResolveEvent(event.id)}
                      disabled={loading}
                    >
                      Resolve
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SecurityEventsTab;
